(() => {
  'use strict';
  if (window.__bearSinisterOpening) return;
  window.__bearSinisterOpening = true;
  const controllers = new WeakMap();
  function init(root) {
    if (controllers.has(root)) return;
    const stage = root.querySelector('.bsm-stage');
    const scene = root.querySelector('.bsm-scene');
    const opening = root.querySelector('.bsm-opening');
    const title = root.querySelector('.bsm-title');
    const followLogo = root.querySelector('.bsm-follow-logo');
    const eventbar = root.querySelector('.bsm-eventbar');
    const hero = root.querySelector('[data-bsm-photo] img');
    const nextImage = root.querySelector('[data-bsm-next-image]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const supported = CSS.supports('animation-timeline', 'view()') && CSS.supports('timeline-scope', '--bsm-scene');
    let failed = false;
    let disposed = false;
    let frame = 0;
    let openingPassed = false;
    let logoGeometry = '';
    let openingStart = 0;
    let openingVisible;
    // At the top, use the actual hero mark. Safari can report negative scroll
    // during rubber banding; an inactive fixed animation must never own that state.
    function syncOpeningVisibility() {
      const visible = Math.max(0, window.scrollY) <= openingStart + 1;
      if (visible === openingVisible) return;
      openingVisible = visible;
      root.classList.toggle('is-opening-visible', visible);
    }
    // One entrance per artist. Content stays visible without JS,
    // on restored scroll positions, during fast scrolling, and with reduced motion.
    const entrances = new Set();
    const entranceTargets = [...root.closest('.bs3').querySelectorAll('.bs3__artist')];
    const entranceObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entranceObserver.unobserve(entry.target);
        if (reduced.matches || root.dataset.bsmMotionDisabled === 'true' || entry.boundingClientRect.top < innerHeight * .45) continue;
        const image = entry.target.querySelector('img');
        if (image && (!image.complete || !image.naturalWidth)) continue;
        const columns = innerWidth > 900 ? 3 : innerWidth > 600 ? 2 : 1;
        const column = entranceTargets.indexOf(entry.target) % columns;
        const animation = entry.target.animate([
          {opacity:.65,transform:'translateY(24px)'},
          {opacity:1,transform:'translateY(0)'}
        ],{duration:500,delay:column * 50,fill:'backwards',easing:'cubic-bezier(0.23, 1, 0.32, 1)'});
        entrances.add(animation);
        animation.finished.then(()=>entrances.delete(animation),()=>entrances.delete(animation));
      }
    },{threshold:0,rootMargin:'0px 0px -24px 0px'});
    entranceTargets.forEach(target=>entranceObserver.observe(target));
    function configure() {
      frame = 0;
      if (disposed) return;
      const allowed = supported && !failed && !reduced.matches && root.dataset.bsmMotionDisabled !== 'true';
      if (reduced.matches || root.dataset.bsmMotionDisabled === 'true') entrances.forEach(animation=>animation.cancel());
      // Reserve the scene before media decoding. No viewport-height switch or scroll handler.
      root.classList.toggle('is-motion-layout', allowed);
      const style = getComputedStyle(root);
      const headerHeight = parseFloat(style.getPropertyValue('--height-header')) || 0;
      const introOffset = Math.max(0, scene.getBoundingClientRect().top + window.scrollY - headerHeight);
      openingStart = introOffset;
      root.style.setProperty('--bsm-intro-offset', `${introOffset}px`);
      const compactWidth = parseFloat(style.getPropertyValue('--bsm-dock-width'));
      const gutter = parseFloat(getComputedStyle(opening).paddingLeft);
      const titleBox = title.getBoundingClientRect();
      const stageBox = stage.getBoundingClientRect();
      root.style.setProperty('--bsm-lede-height', `${root.querySelector('.bsm-lede').offsetHeight}px`);
      root.style.setProperty('--bsm-title-x', `${titleBox.left - gutter}px`);
      root.style.setProperty('--bsm-title-y', `${titleBox.top - stageBox.top - 8}px`);
      root.style.setProperty('--bsm-title-scale', String(titleBox.width / compactWidth));
      root.style.setProperty('--bsm-footer-space', `${stage.offsetHeight - eventbar.offsetTop + (innerWidth <= 700 ? 24 : 32)}px`);
      // Scroll geometry must remain correct even while media is still decoding.
      root.classList.toggle('is-animated', allowed);
      root.classList.toggle('is-docked', !allowed && openingPassed);
      // Rebuild the composited keyframes when their geometry changes. Otherwise
      // Chromium can retain the desktop scale after switching to a mobile width.
      const geometry = [titleBox.width, titleBox.top - stageBox.top, compactWidth, introOffset, gutter].join('|');
      if (allowed && geometry !== logoGeometry) {
        followLogo.style.animationName = 'none';
        getComputedStyle(followLogo).animationName;
        followLogo.style.removeProperty('animation-name');
      }
      logoGeometry = geometry;
      syncOpeningVisibility();
    }
    function schedule() {if (!frame) frame = requestAnimationFrame(configure);}
    // Keep native lazy loading as the fallback. Once the opening is decoded,
    // prepare a bounded lookahead for quick swipes, without high-priority floods.
    const mediaObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || disposed) continue;
        entry.target.fetchPriority = 'high';
        entry.target.loading = 'eager';
        mediaObserver.unobserve(entry.target);
      }
    }, {rootMargin:'8000px 0px', threshold:0});
    async function prepareNext() {
      try {
        await Promise.all([hero.decode(), title.querySelector('img').decode()]);
        if (disposed) return;
        nextImage.fetchPriority = 'high';
        nextImage.loading = 'eager';
        root.querySelector('.bsm-stage-mark').fetchPriority = 'high';
        root.closest('.bs3').querySelectorAll('img[loading="lazy"]').forEach(image => {
          if (!image.closest('.bsm, dialog')) mediaObserver.observe(image);
        });
      } catch (_) {
        // A network failure must not collapse an established scroll scene.
        // Native image loading and the readable static fallback remain available.
      }
    }
    const dockObserver = new IntersectionObserver(entries => {
      openingPassed = entries[0].boundingClientRect.bottom <= 0;
      schedule();
    }, {threshold:0});
    dockObserver.observe(opening);
    const heroFailed = () => { failed = true; schedule(); };
    hero.addEventListener('error', heroFailed, { once: true });
    if (hero.complete) prepareNext();
    else hero.addEventListener('load', prepareNext, {once:true});
    const observer = new ResizeObserver(schedule);
    observer.observe(opening);
    observer.observe(title);
    document.querySelectorAll('.shopify-section-group-header-group').forEach(element => observer.observe(element));
    reduced.addEventListener('change',schedule);
    root.addEventListener('bsm:motion-preference',schedule);
    window.addEventListener('resize',schedule,{passive:true});
    window.addEventListener('scroll',syncOpeningVisibility,{passive:true});
    window.addEventListener('pageshow',schedule);
    document.fonts?.ready.then(schedule);
    configure();
    controllers.set(root,() => {
      disposed = true;cancelAnimationFrame(frame);observer.disconnect();dockObserver.disconnect();entranceObserver.disconnect();mediaObserver.disconnect();entrances.forEach(animation=>animation.cancel());
      root.classList.remove('is-animated','is-motion-layout','is-docked','is-opening-visible');
      reduced.removeEventListener('change',schedule);
      root.removeEventListener('bsm:motion-preference',schedule);
      hero.removeEventListener('load',prepareNext);
      hero.removeEventListener('error',heroFailed);
      window.removeEventListener('resize',schedule);
      window.removeEventListener('scroll',syncOpeningVisibility);
      window.removeEventListener('pageshow',schedule);
      controllers.delete(root);
    });
  }
  const boot = scope => scope.querySelectorAll('[data-bsm-root]').forEach(init);
  // Deferred section code runs after parsing, before unrelated app initialization.
  function bootParsed() {
    if (document.readyState === 'loading') return;
    document.removeEventListener('readystatechange', bootParsed);
    boot(document);
  }
  if (document.readyState === 'loading') document.addEventListener('readystatechange', bootParsed);
  else bootParsed();
  document.addEventListener('shopify:section:load',event=>boot(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-bsm-root]').forEach(root=>controllers.get(root)?.()));
})();
